// ENG-6024: restart trigger for Teams behaviour-field changes.
//
// The webapp's Teams "Advanced" panel saves thread_auto_follow /
// channel_response_mode / adaptive_cards_enabled / adaptive_cards_ask_user_enabled
// via POST /channels/msteams/update. The manager's channel-cred hash correctly
// invalidates on the change and the adapter rewrites the teams-channel entry's
// env in .mcp.json — but the running teams-channel MCP child read
// MSTEAMS_THREAD_AUTO_FOLLOW (and friends) from process.env exactly once at
// boot, so the on-disk rewrite never reaches it. The same last-hop gap
// ENG-5858 closed for sender_policy: the channel-set restart only fires on
// membership change, and the .mcp.json drift watcher deliberately suppresses
// value-only restarts (ENG-5537, Composio URL-rotation flapping).
//
// This trigger hashes ONLY the operator-editable behaviour subset below, so
// credential/token churn in the wider channel config can't fire it — and the
// subset itself only changes on an explicit operator save, so it can't flap.
//
// `ack_reaction` is intentionally EXCLUDED: the claude-code adapter doesn't
// emit an env var for it and teams-channel.ts doesn't read it (the inbound ack
// is a typing activity) — restarting on a field with no runtime consumer would
// be a wasted, user-visible blip.

/**
 * The Teams behaviour fields that map to teams-channel MCP boot-time env vars
 * (claudecode adapter `writeChannelCredentials`, msteams branch). Values are
 * normalised to the adapter's own absent-field defaults so an explicit save of
 * the default value hashes identically to the field being absent — the env
 * output is the same, so no restart is warranted.
 */
export interface MsTeamsBehaviourSubset {
  thread_auto_follow: string;
  channel_response_mode: string;
  adaptive_cards_enabled: boolean;
  adaptive_cards_ask_user_enabled: boolean;
}

export function extractMsTeamsBehaviourSubset(
  config: Record<string, unknown> | null | undefined,
): MsTeamsBehaviourSubset {
  const adaptiveCardsEnabled = config?.['adaptive_cards_enabled'] === true;
  return {
    thread_auto_follow:
      (config?.['thread_auto_follow'] as string | undefined) ?? 'off',
    channel_response_mode:
      (config?.['channel_response_mode'] as string | undefined) ?? 'mention_only',
    adaptive_cards_enabled: adaptiveCardsEnabled,
    // The adapter only emits MSTEAMS_ADAPTIVE_CARDS_ASK_USER_ENABLED when the
    // parent flag is also on — mirror that so toggling ask_user while
    // Adaptive Cards is off (a no-op for the env) doesn't restart.
    adaptive_cards_ask_user_enabled:
      adaptiveCardsEnabled && config?.['adaptive_cards_ask_user_enabled'] === true,
  };
}

/**
 * ENG-7779: whether the behaviour subset actually restricts anything.
 * Deliberately always false: unlike the Slack subset (whose `allowed_users`
 * is an access-control gate), every Teams behaviour field is
 * cosmetic/behavioural - an unknown delivery baseline exposes nothing, so
 * first poll re-baselines without a restart. A named classifier (rather than
 * a hardcoded `false` at the call site) keeps the decision input honest and
 * gives a future access-control field one obvious place to land.
 */
export function isMsTeamsBehaviourRestrictive(_subset: MsTeamsBehaviourSubset): boolean {
  return false;
}

// The restart decision itself (first-poll baseline, no-change debounce,
// persistent/claude-code/session-healthy gates, same-tick restart
// suppression) is identical to the sender-policy trigger — reuse it under a
// call-site-accurate name rather than duplicating the tree.
export { decideSenderPolicyRestart as decideMsTeamsBehaviourRestart } from './sender-policy-restart-decision.js';
